/**
 * Emit hosts for the `survey` analytics group — 6 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { OnboardingStep } from '@/models/analytics/features/onboarding';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type OrganisationRole, type OrganisationSize } from '@heidi/schema/ml-scribe';

export function trackOrganisationSurveyEhrProviderSelected(provider: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Survey,
    props: {
      action: SegmentActionName.OrganisationSurveyEhrProviderSelected,
      ehrProvider: provider,
    },
  });

  // V2 — survey_updated
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SurveyUpdated,
    props: {
      update_action: 'ehr_provider_selected',
      value: provider,
    },
  });
}

export function trackOrganisationSurveyNameEntered(name: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Survey,
    props: {
      action: SegmentActionName.OrganisationSurveyNameEntered,
      organisationName: name,
    },
  });

  // V2 — survey_updated
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SurveyUpdated,
    props: {
      update_action: 'name_entered',
      value: name,
    },
  });
}

export function trackOrganisationSurveyRoleSelected(role: OrganisationRole) {
  trackLegacyEvent({
    name: SegmentObjectName.Survey,
    props: {
      action: SegmentActionName.OrganisationSurveyRoleSelected,
      organisationRole: role,
    },
  });

  // V2 — survey_updated
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SurveyUpdated,
    props: {
      update_action: 'role_selected',
      value: role,
    },
  });
}

export function trackOrganisationSurveyShown() {
  trackLegacyEvent({
    name: SegmentObjectName.Survey,
    props: {
      action: SegmentActionName.OrganisationSurveyShown,
    },
  });

  // V2 — survey_viewed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SurveyViewed,
    props: {
      view_action: 'organisation_survey_shown',
    },
  });
}

export function trackOrganisationSurveySizeSelected(size: OrganisationSize) {
  trackLegacyEvent({
    name: SegmentObjectName.Survey,
    props: {
      action: SegmentActionName.OrganisationSurveySizeSelected,
      organisationSize: size,
    },
  });

  // V2 — survey_updated
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SurveyUpdated,
    props: {
      update_action: 'size_selected',
      value: size,
    },
  });
}

export function trackOrganisationSurveySubmitted(input: {
  name: string;
  size?: OrganisationSize;
  role?: OrganisationRole;
  ehrProvider?: string;
  specialtyId?: string;
  specialtyName?: string;
  otherSpecialty?: string;
  isOfficialPractice?: boolean;
  cqcPracticeId?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Survey,
    props: {
      action: SegmentActionName.OrganisationSurveySubmitted,
      organisationName: input.name,
      organisationSize: input.size,
      organisationRole: input.role,
      ehrProvider: input.ehrProvider,
      specialtyId: input.specialtyId,
      specialtyName: input.specialtyName,
      otherSpecialty: input.otherSpecialty,
    },
  });

  track({
    options: { amplitude: false },
    event_name: TrackingEventName.OrganisationSurveySubmitted,
    props: {
      organisation_name: input.name,
      organisation_size: input.size,
      organisation_role: input.role,
      ehr_provider: input.ehrProvider,
      specialty_id: input.specialtyId,
      specialty_name: input.specialtyName,
      other_specialty: input.otherSpecialty,
      is_official_practice: input.isOfficialPractice,
      cqc_practice_id: input.cqcPracticeId,
    },
  });

  // V2 — survey_submitted
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.SurveySubmitted,
    props: {
      survey_type: 'organisation_survey',
    },
  });

  track({
    event_name: TrackingEventName.OnboardingStepCompleted,
    props: {
      step: OnboardingStep.CUSTOMIZE_ORGANIZATION,
      organisation_size: input.size,
      organisation_role: input.role,
      ehr_provider: input.ehrProvider,
    },
  });
}
